/**
 * Telegram Mini App (SPEC §5 P1, §2).
 * Тонкая обёртка: приложение работает и в обычном браузере как PWA,
 * поэтому каждый вызов безопасен при отсутствии Telegram.
 */

interface TelegramInsets {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

type SafeAreaEvent = 'safeAreaChanged' | 'contentSafeAreaChanged' | 'fullscreenChanged';

interface TelegramWebApp {
  safeAreaInset?: TelegramInsets;
  contentSafeAreaInset?: TelegramInsets;
  viewportHeight?: number;
  viewportStableHeight?: number;
  onEvent?: (event: SafeAreaEvent | 'viewportChanged', callback: () => void) => void;
  offEvent?: (event: SafeAreaEvent | 'viewportChanged', callback: () => void) => void;
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name?: string; last_name?: string } };
  colorScheme?: 'light' | 'dark';
  version?: string;
  platform?: string;
  ready: () => void;
  expand: () => void;
  close: () => void;
  isExpanded?: boolean;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  disableVerticalSwipes?: () => void;
  enableClosingConfirmation?: () => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function tg(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function isTelegram(): boolean {
  const app = tg();
  return Boolean(app && app.initData && app.initData.length > 0);
}

function syncSafeArea(app: TelegramWebApp | null): void {
  const style = document.documentElement.style;
  for (const edge of ['top', 'bottom', 'left', 'right'] as const) {
    for (const [prefix, insets] of [
      ['device', app?.safeAreaInset],
      ['content', app?.contentSafeAreaInset],
    ] as const) {
      const value = insets?.[edge];
      const property = `--app-${prefix}-inset-${edge}`;
      if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        style.setProperty(property, `${value}px`);
      } else {
        style.removeProperty(property);
      }
    }
  }
}

const safeAreaEvents: SafeAreaEvent[] = ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged'];
let safeAreaApp: TelegramWebApp | null = null;
const onSafeAreaChanged = () => syncSafeArea(safeAreaApp);

export function initTelegram(): void {
  const app = tg();
  if (safeAreaApp !== app) {
    for (const event of safeAreaEvents) {
      safeAreaApp?.offEvent?.(event, onSafeAreaChanged);
      app?.onEvent?.(event, onSafeAreaChanged);
    }
    safeAreaApp = app;
  }
  syncSafeArea(app);
  if (!app) return;
  app.ready();
  app.expand();
  // Тема приложения тёмная по умолчанию — красим и обвязку Telegram.
  app.setHeaderColor?.('#101218');
  app.setBackgroundColor?.('#101218');
  // Свайп вниз внутри мини-аппа не должен закрывать окно на длинных экранах.
  app.disableVerticalSwipes?.();
}

export function syncTelegramTheme(theme: 'dark' | 'light'): void {
  const color = theme === 'dark' ? '#101218' : '#f2f3f7';
  tg()?.setHeaderColor?.(color);
  tg()?.setBackgroundColor?.(color);
}

export function haptic(kind: 'light' | 'medium' | 'heavy' = 'light'): void {
  tg()?.HapticFeedback?.impactOccurred(kind);
}

export function hapticSuccess(): void {
  tg()?.HapticFeedback?.notificationOccurred('success');
}

/** Системная кнопка «Назад» Telegram: показываем на вложенных экранах. */
export function useTelegramBackButton(handler: (() => void) | null): void {
  const app = tg();
  if (!app?.BackButton) return;
  if (!handler) {
    app.BackButton.hide();
    return;
  }
  app.BackButton.show();
  app.BackButton.onClick(handler);
}

export function guessTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
  } catch {
    return 'Europe/Moscow';
  }
}
